import { Button, Form } from '@heroui/react'
import { CreateUserDTO, usePublicCreateTenant } from '@qse/api'
import {
  AppRoutesList,
  validateCompanyName,
  validateEmail,
  validateName,
  validatePassword,
  validatePasswordConfirmation,
  validatePhoneNumber,
} from '@qse/constants'
import { HeroInput, LogoIcon } from '@qse/ui'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

interface RegisterScreenProps {
  onSuccess?: (response: CreateUserDTO) => void
}

const Register: React.FC<RegisterScreenProps> = () => {
  const navigate = useNavigate()
  const { mutate: createTenant, isPending: isCreatingTenant } = usePublicCreateTenant()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.target as HTMLFormElement)
    const values = Object.fromEntries(formData)

    // Validate passwords match before submission
    if (password !== confirmPassword) {
      return
    }

    createTenant(
      {
        name: values.name as string,
        firstName: values.firstName as string,
        lastName: values.lastName as string,
        phoneNumber: values.phoneNumber as string,
        adminEmail: (values.adminEmail as string).toLowerCase().trim(),
        adminPassword: password,
      },
      {
        onSuccess: response => {
          navigate(AppRoutesList.confirmEmail, {
            state: {
              email: response.adminEmail,
            },
          })
        },
      },
    )
  }

  return (
    <div className="h-full">
      <div className="sticky top-0 flex lg:justify-end justify-between z-50 w-full px-4 py-4 dark:bg-[#000] bg-white">
        <div className="lg:hidden block">
          <LogoIcon className="text-black dark:text-white" />
        </div>
        <Button
          as={Link}
          className="w-[125px] font-medium"
          color="primary"
          radius="sm"
          to="/auth/login"
        >
          Log in
        </Button>
      </div>
      <div className="flex items-center lg:my-40 lg:pb-24 h-[calc(100vh-72px)] w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Welcome to the QSE GROUP Platform!</h1>

            <p className="text-[15px]">Step-1: Please enter your Company information</p>
          </div>

          <Form className="w-full h-full" validationBehavior="native" onSubmit={handleSubmit}>
            <div className="w-full">
              <div className="flex flex-col gap-[30px] w-full">
                <HeroInput
                  isRequired
                  control="text"
                  label="Company Name"
                  name="name"
                  placeholder="Enter company name"
                  radius="sm"
                  size="md"
                  validate={validateCompanyName}
                  variant="bordered"
                />
                <div className="grid grid-cols-2 gap-[30px]">
                  <HeroInput
                    isRequired
                    control="text"
                    label="First Name"
                    name="firstName"
                    placeholder="Enter first name"
                    radius="sm"
                    size="md"
                    validate={validateName('First Name')}
                    variant="bordered"
                  />
                  <HeroInput
                    isRequired
                    control="text"
                    label="Last Name"
                    name="lastName"
                    placeholder="Enter last name"
                    radius="sm"
                    size="md"
                    validate={validateName('Last Name')}
                    variant="bordered"
                  />
                </div>

                <HeroInput
                  isRequired
                  control="phone"
                  label="Phone Number"
                  name="phoneNumber"
                  placeholder="Enter phone number (e.g. +1 555 555 5555)"
                  radius="sm"
                  size="md"
                  validate={validatePhoneNumber}
                  variant="bordered"
                />
                <HeroInput
                  isRequired
                  control="email"
                  label="Email"
                  name="adminEmail"
                  placeholder="Enter email"
                  radius="sm"
                  size="md"
                  validate={validateEmail}
                  variant="bordered"
                />
                <HeroInput
                  isRequired
                  control="password"
                  label="Password"
                  name="adminPassword"
                  placeholder="Enter password"
                  radius="sm"
                  size="md"
                  validate={validatePassword}
                  value={password}
                  variant="bordered"
                  onChange={setPassword}
                />
                <HeroInput
                  isRequired
                  control="password"
                  label="Re-type Password"
                  name="retypePassword"
                  placeholder="Re-type password"
                  radius="sm"
                  size="md"
                  validate={validatePasswordConfirmation(password)}
                  value={confirmPassword}
                  variant="bordered"
                  onChange={setConfirmPassword}
                />
              </div>

              <div className="mt-5">
                <Button
                  className="w-full font-medium"
                  color="primary"
                  isLoading={isCreatingTenant}
                  radius="sm"
                  type="submit"
                >
                  Create Account
                </Button>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default Register
